import React from 'react';
import { Icon, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { NavLinkAdapter, FuseUtils } from '@fuse';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from 'app/store/actions';
import FuseNavBadge from './../FuseNavBadge';
import { useIntl } from 'react-intl';
import { localizedItem } from '../localizedMenu';
import { useIsInLimboPeriod, useIsPlacePendingActivateHasAccess } from 'app/auth/authHooks';
import { useIsPlaceRestricted } from 'commons/useIsPlaceRestricted';
import * as R from 'ramda';

const useStyles = makeStyles(theme => ({
  item: props => ({
    height: 40,
    ...(theme.direction === 'ltr'
      ? { paddingLeft: `${props.listItemPadding}px` }
      : { paddingRight: `${props.listItemPadding}px` }),
    '&.active': {
      ...(theme.direction === 'ltr'
        ? { paddingLeft: `${props.listItemPadding - 6}px` }
        : { paddingRight: `${props.listItemPadding - 6}px` }),
      borderLeft: '6px solid',
      color: theme.palette.secondary.main + '!important',
      transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
      '& .list-item-text-primary': {
        color: 'inherit',
      },
      '& .list-item-icon': {
        color: 'inherit',
      },
    },
    '& .custom-icon': {
      backgroundColor: 'inherit',
    },
    '&.square, &.active.square': {
      width: '100%',
      borderRadius: '0',
    },
    '& .list-item-icon': {},
    '&.active .list-item-text-primary': {
      fontWeight: '600 !important',
    },
    color: theme.palette.text.primary,
    cursor: 'pointer',
    textDecoration: 'none!important',
  }),
  mainColorIcon: () => ({
    color: theme.palette.secondary.main,
  }),
  icon: {
    fontSize: '2.5rem',
    color: theme.palette.negative.main,
    marginRight: '5px',
  },
}));

function NavigationItem(props) {
  const dispatch = useDispatch();
  const userRole = useSelector(({ auth }) => auth.user.role);
  const direction = useSelector(({ fuse }) => fuse.settings.defaults.direction);
  const isInLimboPeriod = useIsInLimboPeriod();
  const isPlaceRestricted = useIsPlaceRestricted();

  const { item = {}, nestedLevel, active } = props;
  let listItemPadding = nestedLevel > 0 ? Math.min(24 + nestedLevel * 16, 60) : 24;
  const classes = useStyles({ ...props, direction, listItemPadding });

  const isPendingActiveMenu = useIsPlacePendingActivateHasAccess();

  const { formatMessage } = useIntl();

  const checkDisabledMenu = () => {
    if (isPendingActiveMenu && R.includes(item.id, ['profile-info'])) {
      //['profile-info', 'dashboard']
      return !isPendingActiveMenu;
    }

    return isPendingActiveMenu || isInLimboPeriod || isPlaceRestricted;
  };

  if (!FuseUtils.hasPermission(item.auth, userRole) || R.prop('hidden', item)) {
    return null;
  }

  const isDisabled = checkDisabledMenu();

  return (
    <ListItem
      button
      component={NavLinkAdapter}
      to={item.url}
      activeClassName="active"
      className={clsx(classes.item, 'list-item', active, 'subMenuSidebarMenu')}
      disabled={isDisabled}
      onClick={() => dispatch(Actions.navbarCloseMobile())}
      exact={item.exact}
    >
      {R.includes(item.id, ['profile-info']) && (
        <Icon
          className={clsx(
            classes.mainColorIcon,
            'list-item-icon text-16 flex-shrink-0 ltr:mr-16 rtl:ml-16'
          )}
        >
          {item.icon}
        </Icon>
      )}

      {item.customIcon && (
        <img
          alt="logo"
          className={clsx(
            classes.mainColorIcon,
            'list-item-img text-16 flex-shrink-0 ltr:mr-16 custom-icon w-16'
          )}
          src={item.customIcon}
        />
      )}
      <ListItemText
        className="list-item-text"
        primary={localizedItem(item, formatMessage)}
        classes={{ primary: 'text-14 list-item-text-primary' }}
      />
      {item.badge && <FuseNavBadge badge={item.badge} />}
    </ListItem>
  );
}

NavigationItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string,
    url: PropTypes.string,
    localizedId: PropTypes.string,
  }),
};

const NavVerticalItem = withRouter(React.memo(NavigationItem));

export default NavVerticalItem;
